// User info, sign-out API calls.

import type { UserInfo } from "../types/config";
import { readUserFeatures } from "./userFeatures";
import { viewerKindOf } from "./viewerKind";
import {
    AccountDeactivatedError,
    AccountExpiredError,
    AccountPendingApprovalError,
    markSessionEstablished,
} from "./apiClient";

export { buildHostedAuthPath, type HostedAuthScreen } from "./hostedAuth";

export async function requestPasswordReset(email: string): Promise<void> {
    const res = await fetch("/api/password-reset/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
    });

    const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        error?: string;
    };
    if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to request password reset.");
    }
}

export async function confirmPasswordReset(
    token: string,
    password: string,
): Promise<void> {
    const res = await fetch("/api/password-reset/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
    });

    const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        error?: string;
    };
    if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to reset password.");
    }
}

export async function fetchCurrentUser(): Promise<UserInfo> {
    const res = await fetch("/api/me");
    if (!res.ok) {
        if (res.status === 403) {
            const data = await res.json().catch(() => ({}));
            if (data.code === "ACCOUNT_PENDING_APPROVAL") {
                throw new AccountPendingApprovalError(
                    data.message || "Account pending approval",
                );
            }
            if (data.code === "ACCOUNT_DEACTIVATED") {
                throw new AccountDeactivatedError(data.message || "Account deactivated");
            }
            if (data.code === "ACCOUNT_EXPIRED") {
                throw new AccountExpiredError(data.message || "Temporary access ended");
            }
        }
        throw new Error(`fetchCurrentUser failed: ${res.status}`);
    }
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to fetch user");
    // The one call that proves a session exists. Every other 401 is read
    // against this: without it, a 401 is a logged-out visitor on a public page
    // rather than a session that expired under a signed-in user.
    markSessionEstablished();
    // What an administrator switched on for this person, kept to the features
    // this page knows; a server that sends none reads as none on.
    return {
        ...data.user,
        features: readUserFeatures(data.user?.features),
        // The kind of a viewer's grant, read so that a kind this page does not
        // know narrows rather than widens, and a server that names none reads
        // as the management grant every grant then was.
        activeConnectionViewerKind: viewerKindOf(data.user?.activeConnectionRole, data.user?.activeConnectionViewerKind),
        // Whether the open property's account is the caller's to administer:
        // stated by a server that knows scoped access while a property is
        // open, and otherwise not told (null with none open, or absent from an
        // older server), which the account settings read as before.
        activeConnectionWholeAccount:
            typeof data.user?.activeConnectionWholeAccount === "boolean"
                ? data.user.activeConnectionWholeAccount
                : undefined,
    };
}
